import ms, { SymbolOptions } from 'milsymbol';
import { MapEntity } from '../../mapTypes.d';
import { CoordinateUtils } from './CoordinateUtils';

/**
 * Renders milsymbol military symbols to canvas.
 */
export class SymbolRenderer {
    private symbolCache: Map<string, HTMLImageElement> = new Map();

    /**
     * Render a military symbol to an HTMLImageElement.
     *
     * @param code - SIDC (Symbol Identification Coding) code
     * @param size - Symbol size in pixels
     * @param options - Additional milsymbol options
     */
    async renderSymbolToImage(
        code: string,
        size: number,
        options?: SymbolOptions
    ): Promise<HTMLImageElement> {
        const cacheKey = this.getCacheKey(code, size, options);

        if (this.symbolCache.has(cacheKey)) {
            return this.symbolCache.get(cacheKey)!;
        }

        const symbol = new ms.Symbol(code, { size, ...options });
        const svgString = symbol.asSVG();

        const img = await this.loadSvgAsImage(svgString);
        this.symbolCache.set(cacheKey, img);

        return img;
    }

    /**
     * Draw all map entities on the canvas.
     *
     * @param ctx - Canvas 2D rendering context
     * @param entities - Array of map entities to render
     * @param coordUtils - Coordinate utilities for position conversion
     * @param symbolSize - Symbol size in pixels (default 32)
     */
    async drawEntities(
        ctx: CanvasRenderingContext2D,
        entities: MapEntity[],
        coordUtils: CoordinateUtils,
        symbolSize: number = 48
    ): Promise<void> {
        const bounds = coordUtils.getBounds();

        for (const entity of entities) {
            // Skip entities outside the render bounds
            if (!this.isInBounds(entity, bounds)) {
                continue;
            }

            try {
                // Derive the `uniqueDesignation` label from the entity name at
                // draw time, exactly as the live map layers do, so an exported
                // image carries the same labels the operator saw on screen.
                // Built once and reused for both the raster and the anchor so
                // the two cannot disagree about the symbol's bounds.
                const renderOptions: SymbolOptions = {
                    ...entity.markerProperties,
                    ...(entity.name ? { uniqueDesignation: entity.name } : {}),
                };

                const img = await this.renderSymbolToImage(
                    entity.code,
                    symbolSize,
                    renderOptions
                );

                const position = coordUtils.latLngToPixel(
                    entity.latLng.lat,
                    entity.latLng.lng
                );

                // Get the symbol's anchor point for proper positioning
                const symbol = new ms.Symbol(entity.code, { size: symbolSize, ...renderOptions });
                const anchor = symbol.getAnchor();

                // Draw the symbol centered on the anchor point
                ctx.drawImage(
                    img,
                    position.x - anchor.x,
                    position.y - anchor.y
                );
            } catch (error) {
                console.warn(`Failed to render symbol for entity ${entity.id}:`, error);
            }
        }
    }

    /**
     * Load an SVG string as an HTMLImageElement.
     */
    private loadSvgAsImage(svgString: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);

            img.onload = () => {
                URL.revokeObjectURL(url);
                resolve(img);
            };

            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load SVG as image'));
            };

            img.src = url;
        });
    }

    /**
     * Check if an entity is within the render bounds.
     */
    private isInBounds(
        entity: MapEntity,
        bounds: { north: number; south: number; east: number; west: number }
    ): boolean {
        const lat = entity.latLng.lat;
        const lng = entity.latLng.lng;

        return (
            lat >= bounds.south &&
            lat <= bounds.north &&
            lng >= bounds.west &&
            lng <= bounds.east
        );
    }

    /**
     * Generate a cache key for a symbol.
     */
    private getCacheKey(code: string, size: number, options?: SymbolOptions): string {
        return `${code}-${size}-${JSON.stringify(options || {})}`;
    }

    /**
     * Clear the symbol cache.
     */
    clearCache(): void {
        this.symbolCache.clear();
    }
}
